import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  BookOpenIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  AcademicCapIcon,
  HomeIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { getParentDashboard, ParentDashboardData } from '@/api/dashboardApi'
import toast from 'react-hot-toast'

const ParentDashboard: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState<ParentDashboardData | null>(null)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    setLoading(true)
    try {
      const response = await getParentDashboard()
      if (response.success && response.data) {
        setDashboardData(response.data)
      }
    } catch (error) {
      console.error('Dashboard error:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-96 mt-2 animate-pulse"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 animate-pulse">
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const children = dashboardData?.children || []
  const childrenCount = dashboardData?.childrenCount || 0
  const totalActiveBorrowings = dashboardData?.library?.totalActiveBorrowings || 0
  const totalOverdueBorrowings = dashboardData?.library?.totalOverdueBorrowings || 0
  const totalPendingFines = dashboardData?.library?.totalPendingFines || 0
  const childrenStats = dashboardData?.childrenStats || []
  const walletBalance = dashboardData?.wallet?.balance || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HomeIcon className="h-8 w-8 text-purple-600 mr-3" />
            Welcome, {user?.firstName}!
          </h1>
          <p className="text-gray-600 mt-1">
            Monitor your children's school activities
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Today's Date</p>
          <p className="text-lg font-semibold text-gray-900">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {totalOverdueBorrowings > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">Overdue Books Alert</h3>
              <p className="text-sm text-red-700">
                Your children have {totalOverdueBorrowings} overdue book{totalOverdueBorrowings > 1 ? 's' : ''}. Please ensure they are returned.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {totalPendingFines > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4"
        >
          <div className="flex items-center space-x-3">
            <CurrencyDollarIcon className="h-6 w-6 text-amber-600" />
            <div>
              <h3 className="font-semibold text-amber-900">Pending Fines</h3>
              <p className="text-sm text-amber-700">
                Your children have pending fines totaling {formatCurrency(totalPendingFines)}.
              </p>
            </div>
            <Link
              to="/parent/wallet"
              className="ml-auto px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
            >
              Pay Now
            </Link>
          </div>
        </motion.div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Children Count */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">My Children</p>
              <p className="text-2xl font-bold text-gray-900">{childrenCount}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <UserGroupIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-sm text-gray-500">Registered children</span>
          </div>
        </motion.div>

        {/* Active Borrowings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Books Borrowed</p>
              <p className="text-2xl font-bold text-gray-900">{totalActiveBorrowings}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BookOpenIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-3">
            <span className="text-sm text-gray-500">Active borrowings</span>
          </div>
        </motion.div>

        {/* Overdue Books */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Books</p>
              <p className={`text-2xl font-bold ${totalOverdueBorrowings > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {totalOverdueBorrowings}
              </p>
            </div>
            <div className={`p-3 rounded-lg ${totalOverdueBorrowings > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              {totalOverdueBorrowings > 0 ? (
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              ) : (
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              )}
            </div>
          </div>
          <div className="mt-3">
            <span className={`text-sm ${totalOverdueBorrowings > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {totalOverdueBorrowings > 0 ? 'Action required' : 'All on time'}
            </span>
          </div>
        </motion.div>

        {/* Wallet Balance */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Wallet Balance</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(walletBalance)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-3">
            <Link to="/parent/wallet" className="text-sm text-green-600 hover:text-green-800 flex items-center">
              Manage wallet <ArrowRightIcon className="h-3 w-3 ml-1" />
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Children Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AcademicCapIcon className="h-5 w-5 text-purple-600 mr-2" />
            My Children
          </h3>
        </div>

        {children.length === 0 ? (
          <div className="text-center py-8">
            <UserGroupIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No children linked to your account</p>
            <p className="text-sm text-gray-400 mt-1">Contact the school to link your children</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {children.map((child, index) => {
              const childStat = childrenStats.find(s => s.childId === child.id)
              return (
                <motion.div
                  key={child.id || index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {child.firstName} {child.lastName}
                      </h4>
                      {child.studentId && (
                        <p className="text-xs text-gray-500">ID: {child.studentId}</p>
                      )}
                      {(child.grade || child.stream) && (
                        <p className="text-sm text-gray-600 mt-1">
                          {child.grade} {child.stream && `- ${child.stream}`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Child Stats */}
                  <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2">
                    <div className="text-center">
                      <p className="text-lg font-bold text-blue-600">{childStat?.activeBorrowings || 0}</p>
                      <p className="text-xs text-gray-500">Borrowed</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${(childStat?.overdueBorrowings || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {childStat?.overdueBorrowings || 0}
                      </p>
                      <p className="text-xs text-gray-500">Overdue</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-lg font-bold ${(childStat?.pendingFines || 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {formatCurrency(childStat?.pendingFines || 0)}
                      </p>
                      <p className="text-xs text-gray-500">Fines</p>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Link
            to="/parent/wallet"
            className="flex flex-col items-center justify-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors"
          >
            <CurrencyDollarIcon className="h-8 w-8 text-green-600 mb-2" />
            <span className="text-sm font-medium text-green-900">Add Funds</span>
          </Link>
          <Link
            to="/parent/wallet/transactions"
            className="flex flex-col items-center justify-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
          >
            <BookOpenIcon className="h-8 w-8 text-blue-600 mb-2" />
            <span className="text-sm font-medium text-blue-900">Transactions</span>
          </Link>
          <Link
            to="/parent/wallet/invoices"
            className="flex flex-col items-center justify-center p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors"
          >
            <ExclamationTriangleIcon className="h-8 w-8 text-purple-600 mb-2" />
            <span className="text-sm font-medium text-purple-900">Invoices</span>
          </Link>
          <button
            onClick={() => toast.success('Notifications coming soon!')}
            className="flex flex-col items-center justify-center p-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
          >
            <UserGroupIcon className="h-8 w-8 text-amber-600 mb-2" />
            <span className="text-sm font-medium text-amber-900">Notifications</span>
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default ParentDashboard
